import { Button } from '@/components/Button';
import { PhoneInput } from '@/components/PhoneInput';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useRole } from '@/hooks/UseRole';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { Company } from '@/types/company';
import type { CreateUserForm } from '@/types/user';
import { useGroupOptions } from '@/utils/groupOptions';
import { useRoleOptions } from '@/utils/roleOptions';
import { Form } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const CreateUserInformations = ({
  errors,
  touched,
  isSubmitting,
  values,
  handleChange,
}: {
  errors: { [key: string]: string | string[] | undefined };
  touched: { [key: string]: boolean | undefined };
  isSubmitting: boolean;
  values: CreateUserForm;
  handleChange: (value: unknown) => void;
}) => {
  const { t } = useTranslation();
  const isAdmin = useRole('admin');
  const roleOptions = useRoleOptions();
  const groupOptions = useGroupOptions();
  const { companies, loading, listCompanies } = useCompanyStore();

  useEffect(() => {
    if (isAdmin) {
      listCompanies({ paginate: 0 });
    }
  }, [listCompanies, isAdmin]);

  return (
    <div className="flex flex-col">
      <Text className="text-center font-medium text-sky-800 md:text-left">
        {t('createUser:information.title')}
      </Text>
      <Form className="mt-4 grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
        <TextInput
          name="firstname"
          type="text"
          label={t('createUser:information.label.firstname')}
          placeholder={t('createUser:information.placeholder.firstname')}
          error={!!touched.firstname && !!errors.firstname}
          isRequired
        />
        <TextInput
          name="lastname"
          type="text"
          label={t('createUser:information.label.lastname')}
          placeholder={t('createUser:information.placeholder.lastname')}
          error={!!touched.lastname && !!errors.lastname}
          isRequired
        />
        <TextInput
          name="email"
          type="email"
          label={t('createUser:information.label.email')}
          placeholder={t('createUser:information.placeholder.email')}
          error={!!touched.email && !!errors.email}
          isRequired
        />
        <PhoneInput
          name="phone"
          label={t('createUser:information.label.phone')}
          placeholder={t('createUser:information.placeholder.phone')}
          error={!!touched.phone && !!errors.phone}
          isRequired
        />
        <SelectInput
          name="role"
          label={t('createUser:information.label.roles')}
          placeholder={t('createUser:information.placeholder.roles')}
          options={roleOptions}
          defaultValue={values.role}
          onChange={handleChange}
          error={errors.role as string}
          isRequired
        />
        {isAdmin && (
          <>
            <SelectInput
              name="groups"
              label={t('createUser:information.label.groups')}
              options={groupOptions}
              defaultValue={values.groups}
              onChange={handleChange}
              error={errors.groups as string}
              placeholder={t('createUser:information.placeholder.groups')}
              isSearchable
              isMulti
            />
            <SelectInput
              name="company_id"
              label={t('createUser:information.label.company')}
              placeholder={t('createUser:information.placeholder.company')}
              options={companies.map((company: Company) => ({
                value: company.id,
                label: company.name,
              }))}
              defaultValue={values.company_id}
              onChange={handleChange}
              error={errors.company_id as string}
              isLoading={loading}
              isClearable
              isSearchable
            />
          </>
        )}

        <div className="col-span-1 mt-4 w-full md:col-span-2">
          <Button
            type="submit"
            className="w-full lg:w-auto"
            disabled={isSubmitting}
          >
            {t('createUser:information.submit')}
          </Button>
          <Text className="mt-2 text-sm font-light text-gray-500">
            {t('createUser:information.note')}
          </Text>
        </div>
      </Form>
    </div>
  );
};
